import { init } from '@sentry/nuxt';
import { DEBUG_STORAGE_KEY, IGNORED_DOMAINS, SENTRY_IGNORED_ERRORS } from '@/libraries/sentry/config';
import { SentryClientEnvironment } from '@/libraries/sentry';

const xServeTrack = document.cookie.match(/x-serve-track=([^;]+)/)?.[1] ?? null;

/** Returns the appropriate Sentry sample rate based on the environment. */
function getSampleRate(xServeTrack?: string | null) {
	if (typeof localStorage !== 'undefined' && localStorage.getItem(DEBUG_STORAGE_KEY) === 'true') {
		// If user is in debug mode - we send full data.
		return 1.0;
	}

	// Sample all canary errors for the big5 - alpha > epsilon
	// Todo: consider including PR canaries in this
	// Canary-public samples at 10%
	if (xServeTrack?.includes('canary')) return 1.0;

	if (BUILD_CONFIG.env === 'prod') return 0.1;

	return 1.0;
}

/**
 * Returns the correct Sentry environment label for the client.
 *
 * @example
 * // Base environments
 * "{prod|stage}-{SSR|SPA}-nuxt-client"
 * * // PR Canary
 * "pr-canary-{SSR|SPA}-nuxt-client"
 * * // Custom Canary (from xServeTrack header)
 * "{canary-alpha}-nuxt-client"
 */
function getClientEnvironment(xServeTrack?: string | null): SentryClientEnvironment {
	const { env } = BUILD_CONFIG; // prod or stage
	const { BUILD_TARGET } = BUILD_CONFIG.globals; // SSR or SPA

	// Check for canary environment from xServeTrack header
	if (xServeTrack?.includes('canary')) {
		// Consolidate canaries to environment name e.g. canary-alpha
		return `${xServeTrack}-nuxt-client` as SentryClientEnvironment;
	}

	// Check for PR canary environment from config
	if (JW_CONFIG.PR_CANARY_FLAG != null) {
		return `pr-canary-${BUILD_TARGET}-nuxt-client`;
	}

	// Default to prod or stage environment
	return `${env}-${BUILD_TARGET}-nuxt-client`;
}

let release = `jw-app:spa@${BUILD_CONFIG.commitShortHash}`;
if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
	release = `jw-app:ssr-client@${BUILD_CONFIG.commitShortHash}`;
}

init({
	dsn: JW_CONFIG.SENTRY_DSN_SSR_CLIENT,
	release,
	enabled: process.env.NODE_ENV !== 'development',
	environment: getClientEnvironment(xServeTrack),
	maxValueLength: 10000,

	// Error sampling
	sampleRate: getSampleRate(xServeTrack),

	// Performance monitoring - at current rate of 3%, this would represent ~3.5mil transactions/mo
	tracesSampleRate: 0.03,

	// Here we can further filter the transactions we send to Sentry
	// This can limit the noise and costs of Sentry performance monitoring
	beforeSendTransaction: event => {
		const routeName = event.transaction;

		if (!routeName) return null;

		// Remove unnecessary routes
		if (routeName === 'app.refreshtoken') return null;

		// Home route gets a lot of traffic - in theory here we can sample it down to 50% of other pages
		if (routeName === 'app.home' && Math.random() < 0.5) return null;

		return event;
	},

	beforeSend: event => {
		const errorType = event.exception?.values?.[0]?.type || '';

		// ignore abort errors
		if (event.exception?.values?.[0]?.value?.includes('AbortError') || errorType === 'AbortError') return null;
		// ignore errors by name
		if (SENTRY_IGNORED_ERRORS.includes(errorType)) return null;

		// Drop errors from Google Translate mirrors and 3rd parties
		const pageUrl = event.request?.url ?? '';
		if (IGNORED_DOMAINS.some(domain => pageUrl.includes(domain))) {
			return null;
		}

		return event;
	},

	beforeBreadcrumb: breadcrumb => {
		const url = breadcrumb.data?.url;
		if (url && IGNORED_DOMAINS.some(domain => url.includes(domain))) {
			return null;
		}
		return breadcrumb;
	},
});
